import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { DataTable, SearchFilterBar, IdControl, type ColumnDef, type FilterDef } from '@shared-ui/components';
import './Messages.css';

type Delivery = NonNullable<Awaited<ReturnType<typeof adminApi.getNotificationDeliveries>>>['deliveries'][number];

const PAGE_SIZE = 50;

const CHANNEL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'inApp', label: 'In-App' },
  { value: 'sms', label: 'SMS' },
  { value: 'push', label: 'Push' },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'failed', label: 'Failed' },
  { value: 'suppressed', label: 'Suppressed' },
];

const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

// Mirrors EVENT_TYPE from Backend/functions/src/notifications/eventTypes.ts.
// Keep in sync when new event types are added.
const EVENT_TYPE_OPTIONS = [
  { value: 'system.error.critical_alert', label: 'System: Critical Alert' },
  { value: 'system.error.analysis_failed', label: 'System: Analysis Failed' },
  { value: 'parent.progress_report_ready', label: 'Parent: Progress Report' },
  { value: 'user.feedback.thumbs_down', label: 'User: Thumbs Down' },
  { value: 'email.otp', label: 'Email: OTP / Verification' },
  { value: 'email.student_invite', label: 'Email: Student Invite' },
  { value: 'email.tenant_invite', label: 'Email: Tenant Invite' },
  { value: 'email.duel_invite', label: 'Email: Duel Invite' },
];

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '3d', label: '< 3 days' },
  { value: '7d', label: '< 7 days' },
  { value: 'all', label: 'All' },
];

function getSinceIso(range: string): string | undefined {
  if (range === 'all') return undefined;
  const d = new Date();
  if (range === 'today') d.setHours(0, 0, 0, 0);
  else if (range === '3d') d.setDate(d.getDate() - 3);
  else if (range === '7d') d.setDate(d.getDate() - 7);
  return d.toISOString();
}

type MessagesFilters = {
  channel: string;
  status: string;
  priority: string;
  eventType: string;
  dateRange: string;
  recipientEmail: string;
  recipientPhone: string;
  userEmail: string;
};

const defaultFilters = (): MessagesFilters => ({
  channel: '',
  status: '',
  priority: '',
  eventType: '',
  dateRange: '7d',
  recipientEmail: '',
  recipientPhone: '',
  userEmail: '',
});

export function Messages() {
  const [appliedFilters, setAppliedFilters] = useState<MessagesFilters>(defaultFilters);
  const [offset, setOffset] = useState<number>(0);
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);

  const filterDefs = useMemo<FilterDef[]>(() => [
    { key: 'dateRange', kind: 'select', placeholder: 'Date Range', required: true, options: DATE_RANGE_OPTIONS },
    { key: 'channel', kind: 'select', placeholder: 'All Channels', options: CHANNEL_OPTIONS },
    { key: 'status', kind: 'select', placeholder: 'All Statuses', options: STATUS_OPTIONS },
    { key: 'priority', kind: 'select', placeholder: 'All Priorities', options: PRIORITY_OPTIONS },
    { key: 'eventType', kind: 'select', placeholder: 'All Event Types', options: EVENT_TYPE_OPTIONS },
    { key: 'recipientEmail', kind: 'text', placeholder: 'Recipient email' },
    { key: 'recipientPhone', kind: 'text', placeholder: 'Recipient phone' },
    { key: 'userEmail', kind: 'text', placeholder: 'User email' },
  ], []);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['notification-deliveries', appliedFilters, offset],
    queryFn: () => adminApi.getNotificationDeliveries({
      channel: appliedFilters.channel || undefined,
      status: appliedFilters.status || undefined,
      priority: appliedFilters.priority || undefined,
      eventType: appliedFilters.eventType || undefined,
      recipientEmail: appliedFilters.recipientEmail || undefined,
      recipientPhone: appliedFilters.recipientPhone || undefined,
      userEmail: appliedFilters.userEmail || undefined,
      after: getSinceIso(appliedFilters.dateRange),
      limit: PAGE_SIZE,
      offset,
    }),
    enabled: shouldFetch,
    staleTime: Infinity,
  });

  const handleFiltersChange = (values: Record<string, string>) => {
    setAppliedFilters(prev => ({ ...prev, ...(values as Partial<MessagesFilters>) }));
    setOffset(0);
    setShouldFetch(true);
  };

  const totalPages = data
    ? (data.hasMore ? Math.floor(offset / PAGE_SIZE) + 2 : Math.floor(offset / PAGE_SIZE) + 1)
    : 0;

  const eventTypeLabel = useMemo(() => {
    const map = new Map(EVENT_TYPE_OPTIONS.map(o => [o.value, o.label]));
    return (t?: string) => (t ? map.get(t) ?? t : '—');
  }, []);

  const sentToFor = (d: Delivery): string => {
    if (d.channel === 'email') return d.recipient.email || '—';
    if (d.channel === 'whatsapp' || d.channel === 'sms') return d.recipient.phone || '—';
    if (d.channel === 'inApp') return 'in-app';
    // Push is addressed by device, not by contact. One delivery row covers all of
    // a user's devices (see push-notifications.md §2.2), so report the count
    // rather than a token — tokens are opaque and there may be several.
    if (d.channel === 'push') {
      const ids = d.providerMessageId?.replace(/^apns:/, '').split(',').filter(Boolean) ?? [];
      return ids.length ? `${ids.length} device${ids.length > 1 ? 's' : ''}` : 'device';
    }
    return '—';
  };

  const columns = useMemo<ColumnDef<Delivery>[]>(() => [
    { key: 'time', header: 'Time', className: 'msg-time', render: (d) => format(new Date(d.createdAt), 'MMM d HH:mm:ss') },
    { key: 'channel', header: 'Channel', render: (d) => <span className={`msg-channel channel-${d.channel.toLowerCase()}`}>{d.channel}</span> },
    { key: 'status', header: 'Status', render: (d) => <span className={`msg-status status-${d.status}`}>{d.status}</span> },
    { key: 'eventType', header: 'Event Type', className: 'msg-event-type', render: (d) => eventTypeLabel(d.eventType) },
    { key: 'recipient', header: 'Recipient', className: 'msg-recipient', render: (d) => {
      const userId = d.recipient.userId;
      if (!userId) return '—';
      return (
        <span onClick={e => e.stopPropagation()}>
          <IdControl value={userId} container="users" display={d.userEmail || undefined} />
        </span>
      );
    } },
    { key: 'sentTo', header: 'Sent To', className: 'msg-sent-to', render: (d) => sentToFor(d) },
    { key: 'attempts', header: 'Attempts', className: 'msg-attempts', render: (d) => d.attempts },
    { key: 'note', header: 'Note', className: 'msg-note', render: (d) =>
      d.status === 'failed' ? (d.lastError || 'failed')
      : d.status === 'suppressed' ? (d.suppressionReason || 'suppressed')
      : ''
    },
  ], [eventTypeLabel]);

  return (
    <div className="settings-section messages-section">
      <div className="section-header">
        <h2>Messages</h2>
        <p className="section-description">
          All notification deliveries (WhatsApp, Email, In-App, SMS, Push) with their send status and suppression reasons.
        </p>
      </div>

      <SearchFilterBar
        filters={filterDefs}
        values={appliedFilters as unknown as Record<string, string>}
        onChange={handleFiltersChange}
        onFetch={() => refetch()}
        loading={isLoading}
        hideSearch
      />

      {data && (
        <DataTable<Delivery>
          columns={columns}
          data={data.deliveries}
          rowKey={(d) => d.id}
          renderExpandedRow={(d) => (
            <div className="msg-details">
              <div className="detail-item"><strong>ID:</strong> {d.id}</div>
              <div className="detail-item"><strong>Intent ID:</strong> {d.intentId}</div>
              <div className="detail-item"><strong>Event ID:</strong> {d.eventId}</div>
              <div className="detail-item"><strong>Rule ID:</strong> {d.ruleId}</div>
              <div className="detail-item"><strong>Priority:</strong> {d.priority}</div>
              <div className="detail-item"><strong>Template:</strong> {d.templateUsed}</div>
              {d.providerMessageId && <div className="detail-item"><strong>Provider Msg ID:</strong> {d.providerMessageId}</div>}
              {d.recipient.userId && <div className="detail-item"><strong>User ID:</strong> {d.recipient.userId}</div>}
              {d.userEmail && <div className="detail-item"><strong>User Email:</strong> {d.userEmail}</div>}
              {d.recipient.email && <div className="detail-item"><strong>Recipient Email:</strong> {d.recipient.email}</div>}
              {d.recipient.phone && <div className="detail-item"><strong>Recipient Phone:</strong> {d.recipient.phone}</div>}
              {d.sentAt && <div className="detail-item"><strong>Sent:</strong> {format(new Date(d.sentAt), 'PPpp')}</div>}
              {d.deliveredAt && <div className="detail-item"><strong>Delivered:</strong> {format(new Date(d.deliveredAt), 'PPpp')}</div>}
              {d.readAt && <div className="detail-item"><strong>Read:</strong> {format(new Date(d.readAt), 'PPpp')}</div>}
              {d.suppressionReason && (
                <div className="detail-item"><strong>Suppression Reason:</strong> {d.suppressionReason}</div>
              )}
              {d.lastError && (
                <div className="detail-item error-detail">
                  <strong>Error:</strong>
                  <pre>{d.lastError}</pre>
                </div>
              )}
              <div className="detail-item">
                <strong>Rendered Content:</strong>
                <pre>{JSON.stringify(d.renderedContent, null, 2)}</pre>
              </div>
            </div>
          )}
          pagination={data && (data.hasMore || offset > 0) ? {
            page: Math.floor(offset / PAGE_SIZE) + 1,
            totalPages,
            onPageChange: (p) => setOffset((p - 1) * PAGE_SIZE),
          } : undefined}
          compact
          bordered
          emptyMessage="No messages found for the selected filters."
        />
      )}

      {!shouldFetch && (
        <div className="no-logs-message">
          Click "Fetch" to load messages from CosmosDB.
        </div>
      )}
    </div>
  );
}
